/**
 * HRPeerReviewDashboard — full transparency HR view.
 *
 * Shows all peer review assignments (flat list — one row per assignment).
 * Each submitted row has an eye icon that opens a dialog with the full
 * response: answers grouped by topic, overall comment.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch } from "react-redux";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import {
  hrPeerReviewListUrl,
  hrPeerReviewDetailUrl,
  peerReviewCyclesUrl,
  viewActiveUsers,
} from "../../../Service/Apis";
import {
  PeerReviewAssignmentStatus,
  PeerReviewCycle,
} from "./Types/PeerReviewTypes";

// ── Types ────────────────────────────────────────────────────────────────────

interface AssignmentRow {
  id: string;
  cycle: string;
  cycle_name: string;
  reviewer: string;
  reviewer_name: string;
  reviewer_email: string;
  reviewee: string;
  reviewee_name: string;
  reviewee_email: string;
  assigned_by_name: string;
  status: PeerReviewAssignmentStatus;
  submitted_at: string | null;
  created_at: string;
}

interface HRDetailAnswer {
  id: string;
  question: string;
  question_text: string;
  question_type: "likert" | "yes_no";
  topic_name: string;
  likert_rating: number | null;
  yes_no_answer: boolean | null;
  comment: string;
}

interface HRDetailData {
  assignment: AssignmentRow;
  response: {
    id: string;
    is_draft: boolean;
    submitted_at: string | null;
    overall_comment: string;
    reviewer_name: string | null;
    reviewer_email: string | null;
    answers: HRDetailAnswer[];
  } | null;
}

interface ActiveEmployee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

// ── Reusable chip multi-select with searchable dropdown ───────────────────────

interface FilterOption {
  id: string;
  label: string;
}

const FilterMultiSelect: React.FC<{
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  minWidth?: number;
}> = ({ label, options, value, onChange, minWidth = 200 }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      ),
    [options, search]
  );

  const toggle = (id: string) => {
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id]
    );
  };

  const remove = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  return (
    <>
      {/* Trigger box */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          minWidth,
          minHeight: 40,
          border: "1px solid",
          borderColor: open ? "primary.main" : "rgba(0,0,0,0.23)",
          borderRadius: 1,
          px: 1.25,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          cursor: "pointer",
          position: "relative",
          "&:hover": { borderColor: "text.primary" },
          bgcolor: "background.paper",
        }}
      >
        {/* Floating label */}
        <Typography
          component="span"
          sx={{
            position: "absolute",
            top: value.length > 0 ? -9 : "50%",
            left: 10,
            transform: value.length > 0 ? "none" : "translateY(-50%)",
            fontSize: value.length > 0 ? "0.72rem" : "0.875rem",
            color: open ? "primary.main" : "text.secondary",
            bgcolor: "background.paper",
            px: 0.5,
            lineHeight: 1,
            pointerEvents: "none",
            transition: "all 0.15s",
          }}
        >
          {label}
        </Typography>

        {/* Selected chips or placeholder */}
        {value.length === 0 ? (
          <Box sx={{ height: 24 }} />
        ) : (
          value.map((id) => {
            const opt = options.find((o) => o.id === id);
            return (
              <Chip
                key={id}
                label={opt?.label ?? id}
                size="small"
                onDelete={(e) => remove(id, e as unknown as React.MouseEvent)}
                sx={{ fontSize: "0.75rem", height: 24 }}
              />
            );
          })
        )}

        {/* Dropdown arrow */}
        <ExpandMoreIcon
          sx={{
            ml: "auto",
            fontSize: 20,
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Dropdown popover */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => { setOpen(false); setSearch(""); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { width: anchorRef.current?.offsetWidth ?? minWidth, maxHeight: 320, display: "flex", flexDirection: "column" },
        }}
      >
        {/* Search bar */}
        <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Options list */}
        <Box sx={{ overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <Typography variant="caption" sx={{ p: 1.5, display: "block", color: "text.secondary" }}>
              No matches
            </Typography>
          ) : (
            filtered.map((opt) => (
              <MenuItem
                key={opt.id}
                dense
                onClick={() => toggle(opt.id)}
                sx={{ gap: 0.5 }}
              >
                <Checkbox
                  size="small"
                  checked={value.includes(opt.id)}
                  disableRipple
                  sx={{ p: 0.25 }}
                />
                <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: "0.85rem" }} />
              </MenuItem>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const statusColor = (
  s: PeerReviewAssignmentStatus
): "default" | "warning" | "success" | "error" => {
  if (s === "submitted") return "success";
  if (s === "expired") return "error";
  return "warning";
};

const TOPIC_COLORS = [
  "#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c62828", "#00838f",
];

// ── Review Detail Dialog ─────────────────────────────────────────────────────

const ReviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  row: AssignmentRow | null;
  detail: HRDetailData | null;
  loading: boolean;
}> = ({ open, onClose, row, detail, loading }) => {
  // Group answers by topic
  const grouped: Record<string, HRDetailAnswer[]> = {};
  if (detail?.response?.answers) {
    detail.response.answers.forEach((ans) => {
      const key = ans.topic_name || "Uncategorised";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(ans);
    });
  }
  const topics = Object.keys(grouped);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Peer Review Response
          </Typography>
          {row && (
            <Typography variant="body2" color="text.secondary">
              {row.reviewer_name}
              <Typography component="span" sx={{ mx: 0.75 }} color="text.disabled">→</Typography>
              {row.reviewee_name} &nbsp;·&nbsp; {row.cycle_name}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : !detail?.response ? (
          <Alert severity="info">No response submitted yet for this assignment.</Alert>
        ) : (
          <Box>
            {/* Status + submission date */}
            <Box display="flex" gap={1.5} alignItems="center" mb={2} flexWrap="wrap">
              <Chip
                size="small"
                label={detail.response.is_draft ? "Draft saved" : "Submitted"}
                color={detail.response.is_draft ? "warning" : "success"}
                sx={{ fontWeight: 600 }}
              />
              {detail.response.submitted_at && (
                <Typography variant="caption" color="text.secondary">
                  Submitted: {detail.response.submitted_at.slice(0, 10)}
                </Typography>
              )}
            </Box>

            {/* Topics & answers */}
            {detail.response.answers.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No answers recorded yet.
              </Typography>
            ) : (
              topics.map((topicName, ti) => {
                const color = TOPIC_COLORS[ti % TOPIC_COLORS.length];
                const answers = grouped[topicName];
                return (
                  <Paper
                    key={topicName}
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2, borderLeft: `4px solid ${color}`, overflow: "hidden" }}
                  >
                    {/* Topic header */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: `${color}0d`,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
                        {topicName}
                      </Typography>
                    </Box>

                    {/* Questions */}
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          <TableCell sx={{ fontWeight: 600, width: "70%" }}>Question</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Answer</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {answers.map((ans, qi) => (
                          <TableRow key={ans.id} hover>
                            <TableCell>
                              <Box display="flex" gap={1} alignItems="flex-start">
                                <Box
                                  sx={{
                                    mt: "2px",
                                    minWidth: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  {qi + 1}
                                </Box>
                                <Typography variant="body2">
                                  {ans.question_text || "—"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {ans.question_type === "yes_no" ? (
                                <Chip
                                  label={ans.yes_no_answer === null ? "—" : ans.yes_no_answer ? "Yes" : "No"}
                                  size="small"
                                  color={
                                    ans.yes_no_answer === null
                                      ? "default"
                                      : ans.yes_no_answer
                                      ? "success"
                                      : "error"
                                  }
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color={ans.likert_rating ? "text.primary" : "text.disabled"}
                                >
                                  {ans.likert_rating ?? "—"}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                );
              })
            )}

            {/* Overall comment */}
            {detail.response.overall_comment && (
              <Box mt={1}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Overall Comment
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: "grey.50" }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    dangerouslySetInnerHTML={{ __html: detail.response.overall_comment }}
                  />
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const HRPeerReviewDashboard: React.FC = () => {
  const dispatch = useDispatch<any>();

  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [cycles, setCycles] = useState<PeerReviewCycle[]>([]);
  const [employees, setEmployees] = useState<ActiveEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  const [cycleFilter, setCycleFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [reviewerFilter, setReviewerFilter] = useState<string[]>([]);
  const [revieweeFilter, setRevieweeFilter] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  // Dialog state
  const [dialogRow, setDialogRow] = useState<AssignmentRow | null>(null);
  const [detailData, setDetailData] = useState<Record<string, HRDetailData>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});

  // Derived option lists
  const cycleOptions: FilterOption[] = useMemo(
    () => cycles.map((c) => ({ id: c.id, label: c.name })),
    [cycles]
  );
  const statusOptions: FilterOption[] = [
    { id: "pending", label: "Pending" },
    { id: "submitted", label: "Submitted" },
    { id: "expired", label: "Expired" },
  ];
  const employeeOptions: FilterOption[] = useMemo(
    () =>
      employees.map((e) => ({
        id: e.id,
        label: `${e.first_name} ${e.last_name}`.trim() || e.email,
      })),
    [employees]
  );

  const buildFilterParams = (extra?: Record<string, string>) => {
    const params = new URLSearchParams();
    cycleFilter.forEach((id) => params.append("cycle", id));
    statusFilter.forEach((s) => params.append("status", s));
    reviewerFilter.forEach((id) => params.append("reviewer", id));
    revieweeFilter.forEach((id) => params.append("reviewee", id));
    if (extra) Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    return params;
  };

  const buildUrl = () => {
    const params = buildFilterParams({
      page: String(page + 1),
      page_size: String(rowsPerPage),
    });
    return `${hrPeerReviewListUrl}?${params.toString()}`;
  };

  const fetchRows = () => {
    setLoading(true);
    dispatch(fetchData(buildUrl()))
      .then((res: any) => {
        if (res) {
          setRows(res.results ?? res);
          setTotalCount(res.count ?? (res.results ?? res).length);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchCycles = () => {
    dispatch(fetchData(`${peerReviewCyclesUrl}?page_size=100`)).then((res: any) => {
      if (res) setCycles(res.results ?? res);
    });
  };

  const fetchEmployees = () => {
    dispatch(fetchData(`${viewActiveUsers}?page_size=500`)).then((res: any) => {
      if (res) setEmployees(res.results ?? res);
    });
  };

  useEffect(() => {
    fetchCycles();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, cycleFilter, statusFilter, reviewerFilter, revieweeFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // 1. Fetch all assignments matching current filters (no pagination)
      const params = buildFilterParams({ page: "1", page_size: "99999" });
      const listRes = await dispatch(fetchData(`${hrPeerReviewListUrl}?${params.toString()}`));
      const assignments: AssignmentRow[] = listRes?.results ?? listRes ?? [];

      if (assignments.length === 0) return;

      // 2. Fetch full details for every assignment in parallel
      const details: (HRDetailData | null)[] = await Promise.all(
        assignments.map((a) =>
          dispatch(fetchData(hrPeerReviewDetailUrl(a.id)))
            .then((r: any) => (r as HRDetailData) ?? null)
            .catch(() => null)
        )
      );

      // 3. Group by cycle (preserve insertion order = most-recent first)
      const byCycle = new Map<string, { a: AssignmentRow; d: HRDetailData | null }[]>();
      assignments.forEach((a, i) => {
        const key = a.cycle_name || a.cycle;
        if (!byCycle.has(key)) byCycle.set(key, []);
        byCycle.get(key)!.push({ a, d: details[i] });
      });

      // 4. Build XLSX workbook — one sheet per cycle
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();

      byCycle.forEach((rows, cycleName) => {
        // Collect unique questions in appearance order (topic → question order)
        const questionMeta = new Map<
          string,
          { text: string; type: "likert" | "yes_no" }
        >();
        rows.forEach(({ d }) => {
          d?.response?.answers?.forEach((ans) => {
            if (!questionMeta.has(ans.question)) {
              questionMeta.set(ans.question, {
                text: ans.question_text || ans.question,
                type: ans.question_type,
              });
            }
          });
        });
        const questions = Array.from(questionMeta.entries());

        // Header row
        const header: string[] = [
          "Cycle",
          "Reviewer",
          "Reviewee",
          "Status",
          "Submitted At",
          ...questions.map(([, q]) => q.text),
          "Overall Comment",
        ];

        // Data rows
        const dataRows = rows.map(({ a, d }) => {
          // Map question id → formatted answer
          const ansMap: Record<string, string | number> = {};
          d?.response?.answers?.forEach((ans) => {
            ansMap[ans.question] =
              ans.question_type === "yes_no"
                ? ans.yes_no_answer === null
                  ? ""
                  : ans.yes_no_answer
                  ? "Yes"
                  : "No"
                : (ans.likert_rating ?? "");
          });

          // Strip HTML tags from overall comment
          const tmp = document.createElement("div");
          tmp.innerHTML = d?.response?.overall_comment ?? "";
          const plainComment = tmp.textContent || tmp.innerText || "";

          return [
            a.cycle_name,
            a.reviewer_name,
            a.reviewee_name,
            a.status.charAt(0).toUpperCase() + a.status.slice(1),
            a.submitted_at ? a.submitted_at.slice(0, 10) : "",
            ...questions.map(([id]) => ansMap[id] ?? ""),
            plainComment,
          ];
        });

        const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);

        // Bold the question-text header cells specifically
        const qStartCol = 5; // after Cycle, Reviewer, Reviewee, Status, Submitted At
        const qEndCol = 5 + questions.length - 1;
        for (let c = qStartCol; c <= qEndCol; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
          if (cell) cell.s = { font: { bold: true } };
        }

        // Auto column widths (approx)
        ws["!cols"] = header.map((h) => ({ wch: Math.max(h.length + 2, 14) }));

        // Sheet name: Excel limit is 31 chars; sanitise forbidden chars
        const sheetName = cycleName
          .replace(/[:\\/?*[\]]/g, "_")
          .slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      // 5. Trigger download (cellStyles enables bold/font styles)
      const fileName = `peer_reviews_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName, { cellStyles: true });
    } finally {
      setExporting(false);
    }
  };

  const openDialog = (row: AssignmentRow) => {
    setDialogRow(row);
    // Fetch if not already cached
    if (!detailData[row.id] && !detailLoading[row.id]) {
      setDetailLoading((prev) => ({ ...prev, [row.id]: true }));
      dispatch(fetchData(hrPeerReviewDetailUrl(row.id)))
        .then((res: any) => {
          if (res) setDetailData((prev) => ({ ...prev, [row.id]: res as HRDetailData }));
        })
        .finally(() =>
          setDetailLoading((prev) => ({ ...prev, [row.id]: false }))
        );
    }
  };

  const closeDialog = () => setDialogRow(null);

  const hasFilters =
    cycleFilter.length > 0 ||
    statusFilter.length > 0 ||
    reviewerFilter.length > 0 ||
    revieweeFilter.length > 0;

  const clearAllFilters = () => {
    setCycleFilter([]);
    setStatusFilter([]);
    setReviewerFilter([]);
    setRevieweeFilter([]);
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>HR Peer Review Dashboard</PageTitle>

      {/* Filters + Export */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start" mb={3}>
        <FilterMultiSelect
          label="Reviewer"
          options={employeeOptions}
          value={reviewerFilter}
          onChange={(v) => { setReviewerFilter(v); setPage(0); }}
          minWidth={210}
        />
        <FilterMultiSelect
          label="Reviewee"
          options={employeeOptions}
          value={revieweeFilter}
          onChange={(v) => { setRevieweeFilter(v); setPage(0); }}
          minWidth={210}
        />
        <FilterMultiSelect
          label="Cycle"
          options={cycleOptions}
          value={cycleFilter}
          onChange={(v) => { setCycleFilter(v); setPage(0); }}
          minWidth={210}
        />
        <FilterMultiSelect
          label="Status"
          options={statusOptions}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(0); }}
          minWidth={180}
        />

        {hasFilters && (
          <Button
            variant="outlined"
            size="small"
            onClick={clearAllFilters}
            sx={{
              textTransform: "none",
              color: "error.main",
              borderColor: "error.main",
              fontWeight: 600,
              height: 40,
              alignSelf: "flex-start",
              "&:hover": { bgcolor: "error.50", borderColor: "error.dark" },
            }}
          >
            Clear
          </Button>
        )}

        {/* Export button pushed to the right */}
        <Box flexGrow={1} />
        <Button
          variant="contained"
          size="small"
          startIcon={
            exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />
          }
          disabled={exporting}
          onClick={handleExport}
          sx={{
            bgcolor: "#1E7B44",
            "&:hover": { bgcolor: "#155e32" },
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            height: 40,
            alignSelf: "flex-start",
          }}
        >
          {exporting ? "Exporting…" : "Export"}
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                background: "#1E7B44",
                "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
              }}
            >
              <TableCell>Reviewer</TableCell>
              <TableCell>Reviewee</TableCell>
              <TableCell>Cycle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="center">Response</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No peer reviews yet. Employees self-select who they want to review once a cycle is active.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {row.reviewer_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.reviewer_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.reviewee_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.reviewee_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.cycle_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                      color={statusColor(row.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {row.submitted_at ? row.submitted_at.slice(0, 10) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.status === "submitted" || row.status === "pending" ? (
                      <Tooltip title="View response">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openDialog(row)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StandardTablePagination
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* Review detail dialog */}
      <ReviewDialog
        open={!!dialogRow}
        onClose={closeDialog}
        row={dialogRow}
        detail={dialogRow ? (detailData[dialogRow.id] ?? null) : null}
        loading={!!dialogRow && !!detailLoading[dialogRow.id]}
      />
    </Container>
  );
};

export default HRPeerReviewDashboard;
